import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import { Consultation } from '@/core/models/consultation.model'
import { Room } from '@/core/models/room.model'
import { useConsultationRTCConfig } from '@/features/diagnostic'
import { DiagnosticRTCProvider } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import RpiProvider from '@/providers/rpi/RpiProvider'
import { getEnv } from '@/utils/helpers'
import { AllDiagnostics } from '@/pages/diagnostic/containers/AllInOneDiagnostics'
import ConsultationForms from '@/pages/diagnostic/containers/ConsultationForms'
import DiagnosticMedicalVideo from '@/pages/diagnostic/containers/DiagnosticMedicalVideo'
import DiagnosticStethoscope from '@/pages/diagnostic/containers/DiagnosticStethoscope'
import PatientInfo from '@/pages/diagnostic/containers/PatientInfo'
import RpiMesures from '@/pages/diagnostic/containers/RpiMesures'

import { MedicalActsSection } from '@/features/medical-acts/ui/MedicalActsSection'

import { EmergencyWaitingView } from './EmergencyWaitingView'

type EmergencyContentProps = {
  consultationRoom?: Room
  emergencyConsultation?: Consultation
  emergencyRoom: Room
  isCreatingConsultation: boolean
  isStreamer: boolean
  onSelectPatient: (patientId: string) => void
  selectedSidebarMenuItem: string
}

export function EmergencyContent({
  consultationRoom,
  emergencyConsultation,
  emergencyRoom,
  isCreatingConsultation,
  isStreamer,
  onSelectPatient,
  selectedSidebarMenuItem,
}: EmergencyContentProps) {
  const {
    customDevices,
    medicalVideoAttendeeInfo,
    medicalVideoMeetingInfo,
    peerId,
    rpiConfig,
    rpiMeetingId,
    rtcConfig,
    stethoscopeAttendeeInfo,
    stethoscopeMeetingInfo,
  } = useConsultationRTCConfig()

  if (isCreatingConsultation) {
    return (
      <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size="24px" color="info" />
      </Box>
    )
  }

  if (!emergencyRoom.currentConsultation) {
    return <EmergencyWaitingView emergencyRoom={emergencyRoom} onSelectPatient={onSelectPatient} />
  }

  if (!consultationRoom) return null

  switch (selectedSidebarMenuItem) {
    case 'medicalVideo':
      return (
        <DiagnosticRTCProvider
          key="medicalVideo"
          config={{
            chime: {
              attendeeInfo: medicalVideoAttendeeInfo,
              meetingInfo: medicalVideoMeetingInfo,
            },
            peerjs: {
              config: rtcConfig,
              peerId: peerId + 'video',
              streamer: consultationRoom.streamer + 'video',
            },
            rpi: rpiConfig,
          }}
          customDevices={customDevices}
          isStreamer={isStreamer}
          type="video"
        >
          <DiagnosticMedicalVideo consultationId={emergencyRoom.currentConsultation} />
        </DiagnosticRTCProvider>
      )
    case 'mesures':
      return rpiMeetingId ? (
        <RpiProvider isStreamer meetingID={rpiMeetingId} serverUrl={getEnv('VITE_RPI_GATEWAY')!}>
          <RpiMesures consultationId={emergencyRoom.currentConsultation} isStreamer={isStreamer} />
        </RpiProvider>
      ) : (
        <Box sx={{ height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <AllDiagnostics consultationId={emergencyRoom.currentConsultation} isStreamer={isStreamer} />
        </Box>
      )
    case 'patientInfo':
      return <PatientInfo consultationId={emergencyRoom.currentConsultation} showVitaleCard showHistory />
    case 'forms':
      return (
        <Box sx={{ p: 3 }}>
          <ConsultationForms consultationId={emergencyRoom.currentConsultation} />
        </Box>
      )
    case 'medicalActs':
      return (
        <Box sx={{ p: 3 }}>
          <MedicalActsSection patientId={emergencyConsultation?.patient._id || ''} consultationId={emergencyRoom.currentConsultation} />
        </Box>
      )
    case 'stethoscope':
      return (
        <DiagnosticRTCProvider
          key="stethoscope"
          config={{
            chime: {
              attendeeInfo: stethoscopeAttendeeInfo,
              meetingInfo: stethoscopeMeetingInfo,
            },
            peerjs: {
              config: rtcConfig,
              peerId: peerId + 'audio',
              streamer: consultationRoom.streamer + 'audio',
            },
            rpi: rpiConfig,
          }}
          customDevices={customDevices}
          isStreamer={isStreamer}
          type="audio"
        >
          <DiagnosticStethoscope consultationId={emergencyRoom.currentConsultation} />
        </DiagnosticRTCProvider>
      )
    default:
      return null
  }
}
